import { administrationLegacy } from './administrationLegacy';
import { lacEpauleCards, lacEpauleParticipants, WorkshopCard, WorkshopStatus } from './lacEpauleSeptembre2026Data';
import { FINANCE_FD_ID } from '../components/financeFD/card';
import { internalToolsLegacy } from './internalToolsLegacy';
import { thanksBeingLateLegacy } from './thanksBeingLateLegacy';
import { thanksBeingLatePrevious } from './thanksBeingLatePrevious';
import { agentiqueRagLegacy } from './agentiqueRagLegacy';

export const preparationGroups = [
  { id: 'cap', label: 'Cap stratégique & choix d’entreprise' },
  { id: 'clients', label: 'Clients, ventes & croissance' },
  { id: 'operations', label: 'Opérations & qualité de livraison' },
  { id: 'humains', label: 'Humains, leadership & culture' },
  { id: 'outils', label: 'IA, données & outils' },
  { id: 'finances', label: 'Finances, gouvernance & risques' },
  { id: 'sortie', label: 'Décisions & engagements de sortie' },
] as const;
export type PreparationGroup = typeof preparationGroups[number]['id'];
export const initialGroups: Record<string, PreparationGroup> = {
  [FINANCE_FD_ID]: 'finances',
  'midi-formation-agentique': 'humains',
  'plan-action-agentic-ai-client-bz': 'clients',
  'resultats-entreprise-bz': 'finances',
  'forces-humaines-bz': 'humains',
  'comites-bz-raison-etre-rituels': 'sortie',
  'tks-being-late-human-first': 'humains',
  'prisme-master': 'outils',
  'atlas-donnees-bz': 'outils',
  'ia-agentique-rag': 'outils',
  'aikub-positionnement': 'cap',
  'departement-ventes': 'clients',
  'cloud-infra': 'operations',
  'service-ti-sac': 'operations',
  'admin-finance-rh': 'finances',
  'actionnariat-gouvernance': 'finances',
  'acquisitions-xelys-tnt': 'cap',
  'valeurs-culture': 'humains',
  'scope-offre-bz': 'clients',
  'bz-telecom-cyberops-pmbot': 'outils',
};
export type EditableCard = Pick<WorkshopCard, 'title' | 'family' | 'importance' | 'ownerIds' | 'tags' | 'summary' | 'whyNow' | 'enrichedContext' | 'questions' | 'decisions' | 'signals' | 'evidence'>;
export interface PreparationEntry {
  content: EditableCard;
  group: PreparationGroup;
  ready: boolean;
}
export interface PreparationData {
  version: 1;
  workshop: 'lac-epaule-septembre-2026';
  entries: Record<string, PreparationEntry>;
}
export const emptyPreparation = (): PreparationData => ({ version: 1, workshop: 'lac-epaule-septembre-2026', entries: {} });
// Rebase only unchanged fields from the former acquisition seed. Preparation saves
// full snapshots: without this, merely marking the old card ready revives old copy.
// This is a read-only projection; custom notes, grouping, readiness and raw backups survive.
const legacyServiceSac: Partial<EditableCard> = {
  title: 'Service TI / SAC',
  ownerIds: ['jp', 'vb', 'jmc'],
  summary: 'Repenser SAC et Service TI comme expérience client structurée : priorités, escalades, IA, documentation et autonomie.',
  whyNow: 'Le SAC est au cœur de la qualité client. Avec IA et PRIsME, on peut réduire friction et améliorer le suivi.',
  enrichedContext: ['SAC ressort dans PRIsME, TactiCE, AIKUB ERP et AIKUB Web.', 'Des templates d’escalade/procédures existent dans PRIsME.', 'Jean-Pierre, Vincent et Jean-Michel sont directement liés au sujet.'],
  questions: ['Quels irritants clients reviennent toujours?', 'Où l’IA peut aider sans nuire au service humain?', 'Quelle procédure d’escalade doit être officielle?'],
  decisions: ['Définir parcours SAC cible.', 'Prioriser documentation client interne.', 'Choisir métriques de qualité.'],
  signals: ['SAC mentionné dans plusieurs systèmes.', 'TactiCE contient des traces support/opérations.', 'La qualité client doit être industrialisée.'],
  evidence: ['PRIsME templates procédures', 'TactiCE backup SAC', 'AIKUB Web assistant'],
};
const legacyCloud: Partial<EditableCard> = {
    ownerIds: ['rd', 'ed'],
    tags: ['cloud', 'infra', 'sécurité', 'scalabilité'],
    summary: 'Aligner cloud, hébergement, sécurité, accès et résilience avec la montée des bots et des données critiques.',
    whyNow: 'Les bots et APIs augmentent la surface d’exposition. L’infrastructure doit être documentée et opérable.',
    enrichedContext: ['Le repo PRIsME contient plusieurs surfaces serveur/API.', 'Les recherches montrent Cloud dans PRIsME, AIKUB ERP et TactiCE.', 'L’Atlas données met en évidence le besoin de rôles DB/API.'],
    questions: ['Quels systèmes sont critiques et mal documentés?', 'Quel monitoring manque?', 'Quelle séparation dev/staging/prod veut-on?'],
    decisions: ['Lister systèmes critiques.', 'Définir posture backup/monitoring.', 'Valider doctrine secrets et accès.'],
    signals: ['Cloud transversal partout.', 'Bots + DB = risque infra accru.', 'Richard doit être connecté à l’Atlas données.'],
    evidence: ['PRIsME server/*', 'AIKUB ERP services', 'TactiCE exports'],
};
const legacyAcquisitions: Partial<EditableCard> = {
  title: 'Acquisitions — Xelys / TNT / opportunités',
  tags: ['acquisition', 'croissance', 'TNT', 'Xelys', 'risques'],
  summary: 'Regrouper la réflexion acquisition : opportunités, intégration, financement, risques humains et capacité opérationnelle.',
  whyNow: 'Des dossiers récents comme TNT/Stein Monast et d’autres opportunités démontrent que BZ doit avoir une grille claire.',
  enrichedContext: ['Le dossier Stein Monast/TNT a généré un constat one-pager avec prix, earn-out, cautions, risques et due diligence.', 'TactiCE et PRIsME contiennent des traces acquisitions.', 'L’acquisition doit être liée à capacité opérationnelle et culture BZ.'],
  questions: ['Quel type d’acquisition est réellement souhaité?', 'Qui porte l’intégration?', 'Quels critères tuent un deal rapidement?'],
  decisions: ['Créer grille acquisition BZ.', 'Nommer comité intégration.', 'Définir seuils de risque.'],
  signals: ['TNT actif.', 'LOI comporte risques.', 'Croissance externe doit être disciplinée.'],
  evidence: ['Lettre intention Stein Monast', 'PRIsME acquisition matches', 'TactiCE acquisition matches'],
};
const legacyVentes: Partial<EditableCard> = {
    tags: ['ventes', 'revenus', 'CRM', 'prévisibilité'],
    summary: 'Revoir la machine de ventes : pipeline, qualité des opportunités, liens CRM/PRIsME et discipline de suivi.',
    whyNow: 'La croissance IA/PRIsME doit se traduire en offres simples, vendables et suivies.',
    enrichedContext: ['PRIsME contient Directeur Compte et Solutions d’affaires.', 'Les courriels récents montrent des suivis revenus et comparatifs annuels.', 'AIKUB ERP gère clients/opportunités/contacts côté opérations.'],
    questions: ['Quel pipeline est réellement fiable?', 'Quelles offres sont les plus faciles à vendre maintenant?', 'Qu’est-ce qu’Antoine doit recevoir comme support PRIsME/AIKUB?'],
    decisions: ['Définir cadence pipeline.', 'Choisir 3 offres prioritaires.', 'Créer un rituel ventes/SA/production.'],
    signals: ['Sujet revenus récurrent.', 'Directeur Compte existe dans PRIsME.', 'Offres IA/cyber doivent être simplifiées.'],
    evidence: ['PRIsME DirecteurCompte', 'AIKUB ERP opportunities', 'Courriels revenus BZ'],
};
const legacyValeurs: Partial<EditableCard> = {
  tags: ['valeurs', 'culture', 'humain', 'autonomie', 'changement'],
  summary: 'Ramener les valeurs BZ dans une version moderne : simplicité, autonomie, service, responsabilité, adaptation à l’IA.',
  whyNow: 'La transformation IA peut créer de la vitesse, mais aussi de la confusion. La culture doit porter la transformation.',
  enrichedContext: ['Dany privilégie le principe de l’arbre : comprendre le pourquoi, le tronc et les branches avant les détails.', 'AIKUB Web parle de valeur humaine et d’adoption.', 'Les anciennes valeurs à ramener apparaissent dans le screenshot comme thème culturel.'],
  questions: ['Quelles anciennes valeurs BZ doivent revenir?', 'Quel comportement veut-on renforcer chez les gestionnaires?', 'Comment expliquer l’IA sans faire peur?'],
  decisions: ['Choisir 5 valeurs opératoires.', 'Définir comportements observables.', 'Créer message interne post-lac.'],
  signals: ['Culture = frein ou accélérateur.', 'Besoin de réduire charge cognitive.', 'Atelier doit être moins ordre du jour, plus discussion.'],
  evidence: ['Préférence Dany arbre/simple/autonomie', 'AIKUB Web human modal', 'Screenshot lac à l’épaule'],
};
const legacyAtlas: Partial<EditableCard> = {
    title: 'Atlas des données BZ',
    tags: ['database', 'API', 'sécurité', 'bots', 'documentation'],
    summary: 'Utiliser l’Atlas comme point de départ pour documenter les tables, domaines, accès DB directs et accès API gouvernés.',
    whyNow: 'Avec les bots en production, BZ doit documenter ses données avant d’élargir les accès agentiques.',
    enrichedContext: [
      'Le MD BD ERP analysé contient environ 730 tables, plus de 12 000 colonnes et 268 relations Mermaid.',
      'L’Atlas V2 présente déjà une map relationnelle, un inspecteur table, les accès DB directs et les API cibles.',
      'La recommandation technique est de créer des façades API gouvernées avant tout découpage physique massif.'
    ],
    questions: [
      'Quels domaines de données sont les plus sensibles pour les bots?',
      'Quels accès DB directs doivent être interdits aux agents?',
      'Quelle API minimale doit exister pour les 3 premiers domaines critiques?'
    ],
    decisions: ['Prioriser 3 domaines à sécuriser.', 'Définir rôle lecture/écriture par domaine.', 'Valider la doctrine API-first pour les bots.'],
    signals: ['Un seul gros schéma devient difficile à gouverner.', 'Les clients auront le même besoin.', 'Très bon modèle de service futur BZ/AIKUB.'],
    evidence: ['Email BD ERP', 'Atlas des données BZ dans IA Agentic', 'PRIsME data atlas guard'],
};
const legacyAtlasOwners: Partial<EditableCard> = { enrichedContext: ['Le catalogue existant montre les actifs client et leurs dépendances; l’Atlas frontend explore un référentiel statique de domaines, tables et relations.', 'Les portails de demande, approbation, audit et révocation restent à construire. Les API et rôles affichés dans l’Atlas sont des cibles.', 'Répartition cible par domaines, avec Mélissa et François Delisle pour Administration et données clients; responsabilités à valider.', 'NIST AI RMF et NIST CSF 2.0 sont des repères complémentaires, sans certification ni conformité automatique.'] };
const legacyResults: Partial<EditableCard> = {}; // Private financial seed is no longer shipped in the SPA.
const legacyCapital: Partial<EditableCard> = {
    summary: 'Revoir le tableau des actionnaires, les décisions de gouvernance, les seuils sensibles et le récit 2026.',
    whyNow: 'Les dossiers de rachat, scénarios d’actionnariat et croissance nécessitent une compréhension commune sans surcharger le groupe.',
    enrichedContext: ['PRIsME contient des composants liés à actionnariat/scénarios et mémo fiscal.', 'TactiCE contient aussi des traces actionnaires/acquisition.', 'Les discussions CE doivent garder cette information sensible et structurée.'],
    questions: ['Qu’est-ce qui doit être partagé avec tous les gestionnaires versus actionnaires seulement?', 'Quels scénarios 2026 sont ouverts?', 'Quel langage commun veut-on utiliser?'],
    decisions: ['Définir niveau de transparence atelier.', 'Identifier décisions réservées actionnaires.', 'Préparer version vulgarisée du capital.'],
    signals: ['Sujet sensible.', 'Présence dans PRIsME et TactiCE.', 'Doit rester simple et maîtrisé.'],
    evidence: ['ActionnariatScenariosModal', 'MemoFiscalBZ2026Modal', 'TactiCE backup actionnaires'],
};
const previousCapital: Partial<EditableCard> = {
    summary: 'Comprendre qui détient BZ inc., les participations détenues par BZ et les responsabilités de la gouvernance exécutive.',
    whyNow: 'Partager une lecture claire du capital et de l’écosystème sans confondre actionnaires, sociétés et rôles de direction.',
    enrichedContext: ['Capital BZ : sept détenteurs, 100 %; relevé Touski du 23 juin 2026 consulté le 21 septembre.', 'Participations directes : BZ Telecom inc 30 %, Gestion KiloOctets inc 90 % (Touski, 15 juin 2026); Agentique AIKUB inc 20 % (confirmé par Dany, 21 septembre 2026).', 'La vue exécutive reprend l’organigramme Gouvernance CE : liens hiérarchiques et fonctionnels, guide et fiches. Un onglet distinct présente la vision de refonte administration, finances et RH pilotée par Louis-Alexandre, avec Mélissa, François et l’appui du CE avec Dany.'],
    questions: ['Comment expliquer simplement la structure du capital et les participations de BZ?', 'Quelles responsabilités et interfaces faut-il clarifier ensemble?', 'Quelles informations doivent rester réservées au CE et aux actionnaires?'],
    decisions: ['Distinguer détention du capital et responsabilités exécutives.', 'Convenir du niveau de partage approprié.', 'Identifier les précisions à valider à la source.'],
    signals: ['Sept détenteurs de BZ, total de 100 %.', 'Trois participations directes, sans total agrégé.', 'Capital et gouvernance : deux lectures complémentaires.'],
    evidence: ['Touski · organigramme des compagnies · consulté le 21 septembre 2026.', 'Participation BZ de 20 % dans Agentique AIKUB inc confirmée par Dany le 21 septembre 2026.', 'PRIsME Master · Gouvernance CE · Organigramme.'],
};
export function materializeCards(data: PreparationData): WorkshopCard[] {
  const existing = lacEpauleCards.map(card => {
    const saved = data.entries[card.id]?.content;
    const legacy: Partial<EditableCard> | null = card.id === 'admin-finance-rh' ? administrationLegacy : card.id === 'bz-telecom-cyberops-pmbot' ? internalToolsLegacy : card.id === 'ia-agentique-rag' ? agentiqueRagLegacy : card.id === 'tks-being-late-human-first' ? thanksBeingLateLegacy : card.id === 'service-ti-sac' ? legacyServiceSac : card.id === 'cloud-infra' ? legacyCloud : card.id === 'actionnariat-gouvernance' ? legacyCapital : card.id === 'resultats-entreprise-bz' ? legacyResults : card.id === 'acquisitions-xelys-tnt' ? legacyAcquisitions : card.id === 'departement-ventes' ? legacyVentes : card.id === 'valeurs-culture' ? legacyValeurs : card.id === 'atlas-donnees-bz' ? legacyAtlas : null;
    if (!legacy || !saved) return { ...card, ...saved };
    if (card.id === 'actionnariat-gouvernance') {
      const versions = [legacyCapital, previousCapital];
      const projected = { ...saved };
      for (const field of Object.keys(projected) as (keyof EditableCard)[]) {
        const value = projected[field];
        if (versions.some(v => JSON.stringify(value) === JSON.stringify(v[field]))) delete projected[field];
      }
      for (const field of ['enrichedContext', 'questions', 'decisions', 'signals', 'evidence'] as const) {
        const lines = projected[field];
        if (Array.isArray(lines)) projected[field] = lines.map(line => {
          for (const version of versions) {
            const index = version[field]?.indexOf(line) ?? -1;
            if (index >= 0) return card[field][index] ?? line;
          }
          return line;
        });
      }
      return { ...card, ...projected };
    }
    if (card.id === 'tks-being-late-human-first') {
      const versions = [thanksBeingLateLegacy, thanksBeingLatePrevious];
      const projected = Object.fromEntries(Object.entries(saved).filter(([key, value]) => !versions.some(v => JSON.stringify(value) === JSON.stringify(v[key as keyof EditableCard]))));
      for (const field of ['enrichedContext', 'questions', 'decisions', 'signals', 'evidence'] as const) {
        const lines = projected[field];
        if (Array.isArray(lines)) projected[field] = lines.flatMap(line => {
          const version = versions.find(v => v[field]?.includes(line));
          if (!version) return [line];
          const index = version[field]!.indexOf(line);
          return card[field][index] ? [card[field][index]] : [];
        });
      }
      return { ...card, ...projected };
    }
    const overrides = Object.fromEntries(Object.entries(saved).filter(([key, value]) =>
      JSON.stringify(value) !== JSON.stringify(legacy[key as keyof EditableCard]) &&
      (card.id !== 'atlas-donnees-bz' || JSON.stringify(value) !== JSON.stringify(legacyAtlasOwners[key as keyof EditableCard]))
    ));
    if (card.id === 'bz-telecom-cyberops-pmbot' || card.id === 'ia-agentique-rag' || card.id === 'tks-being-late-human-first') {
      const previous = card.id === 'bz-telecom-cyberops-pmbot' ? internalToolsLegacy : card.id === 'ia-agentique-rag' ? agentiqueRagLegacy : thanksBeingLateLegacy;
      for (const field of ['enrichedContext', 'questions', 'decisions', 'signals', 'evidence'] as const) {
        const lines = overrides[field];
        if (Array.isArray(lines)) overrides[field] = lines.flatMap(line => {
          const index = previous[field].indexOf(line);
          return index < 0 ? [line] : card[field][index] ? [card[field][index]] : [];
        });
      }
    }
    if (card.id === 'cloud-infra' || card.id === 'actionnariat-gouvernance') {
      for (const field of ['enrichedContext', 'questions', 'decisions', 'signals', 'evidence'] as const) {
        const lines = overrides[field]; const previous = (card.id === 'cloud-infra' ? legacyCloud : legacyCapital)[field];
        if (Array.isArray(lines) && previous) overrides[field] = lines.map(line => {
          const index = previous.indexOf(line);
          return index < 0 ? line : card[field][index];
        });
      }
    }
    if (card.id === 'resultats-entreprise-bz') {
      // Refresh retained seed lines even when an author appended a personal note.
      // Only exact historical lines are replaced; authored text is never scrubbed.
      for (const field of ['enrichedContext', 'evidence'] as const) {
        const lines = overrides[field];
        const previous = legacyResults[field];
        if (Array.isArray(lines) && previous) overrides[field] = lines.map(line => {
          const index = previous.indexOf(line);
          return index < 0 ? line : card[field][index];
        });
      }
    }
    return { ...card, ...overrides };
  });
  // Retired demo snapshots stay importable, but must never become workshop cards.
  const added = Object.entries(data.entries).filter(([id]) => id !== FINANCE_FD_ID && id !== 'ce-strategic-announcement-2026' && !lacEpauleCards.some(card => card.id === id)).map(([id, entry], i) => ({ ...entry.content, id, position: { x: (i % 3) * 28 + 3, y: 60 + Math.floor(i / 3) * 28, rotate: 0 } }));
  return [...existing, ...added];
}
export function editableContent(card: WorkshopCard): EditableCard {
  const { title, family, importance, ownerIds, tags, summary, whyNow, enrichedContext, questions, decisions, signals, evidence } = card;
  return { title, family, importance, ownerIds, tags, summary, whyNow, enrichedContext, questions, decisions, signals, evidence };
}
export type DiscussionStates = Record<string, WorkshopStatus>;
export const PREPARATION_KEY = 'bz-lac-epaule-septembre-2026-preparation-v1';
export const LEGACY_STATUS_KEY = 'bz-lac-epaule-septembre-2026-statuses';
export interface WorkshopDocument {
  version: 1;
  preparation: PreparationData;
  discussion: DiscussionStates;
  view: 'list' | 'wall';
}
export const emptyWorkshop = (): WorkshopDocument => ({ version: 1, preparation: emptyPreparation(), discussion: {}, view: 'list' });
export function validateWorkshop(value: unknown): WorkshopDocument {
  const data = record(value);
  exactKeys(data, ['version', 'preparation', 'discussion', 'view']);
  if (data.version !== 1 || (typeof data.view !== 'string' || !['list', 'wall'].includes(data.view))) throw new Error('Sauvegarde incompatible.');
  validatePreparation(data.preparation);
  validateStatuses(data.discussion);
  return value as WorkshopDocument;
}
export function loadWorkshop(storage: Pick<Storage, 'getItem'>): { document: WorkshopDocument; raw: string | null } {
  const raw = storage.getItem(PREPARATION_KEY);
  if (raw !== null) return { document: validateWorkshop(JSON.parse(raw)), raw };
  const legacy = storage.getItem(LEGACY_STATUS_KEY);
  return { document: { ...emptyWorkshop(), discussion: legacy ? validateStatuses(JSON.parse(legacy)) : {} }, raw };
}
export function saveWorkshop(storage: Pick<Storage, 'getItem' | 'setItem'>, document: WorkshopDocument, expectedRaw: string | null): string {
  validateWorkshop(document);
  if (storage.getItem(PREPARATION_KEY) !== expectedRaw) throw new Error('Modifié dans un autre onglet. Rechargez avant de sauvegarder.');
  const raw = JSON.stringify(document);
  if (new TextEncoder().encode(raw).byteLength > 2000000) throw new Error('Sauvegarde trop volumineuse (2 Mo maximum).');
  storage.setItem(PREPARATION_KEY, raw);
  return raw;
}


function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Objet JSON invalide.');
  const result = value as Record<string, unknown>;
  if (Object.keys(result).some(key => ['__proto__', 'constructor', 'prototype'].includes(key))) throw new Error('Clé interdite.');
  return result;
}
function exactKeys(value: Record<string, unknown>, keys: string[]) {
  if (Object.keys(value).sort().join('|') !== [...keys].sort().join('|')) throw new Error('Champs manquants ou inconnus.');
}
export function validatePreparation(value: unknown): PreparationData {
  const data = record(value);
  exactKeys(data, ['version', 'workshop', 'entries']);
  if (data.version !== 1 || data.workshop !== 'lac-epaule-septembre-2026') throw new Error('Version ou atelier incompatible.');
  const entries = record(data.entries);
  if (Object.keys(entries).length > 500) throw new Error('Maximum 500 sujets modifiés.');
  for (const [id, raw] of Object.entries(entries)) {
    if (id !== FINANCE_FD_ID && id !== 'ce-strategic-announcement-2026' && !lacEpauleCards.some(card => card.id === id) && !/^local-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id)) throw new Error('Identifiant de sujet invalide.');
    const entry = record(raw);
    exactKeys(entry, ['content', 'group', 'ready']);
    if (!preparationGroups.some(group => group.id === entry.group) || typeof entry.ready !== 'boolean') throw new Error('Groupe ou préparation invalide.');
    const content = record(entry.content);
    exactKeys(content, ['title', 'family', 'importance', 'ownerIds', 'tags', 'summary', 'whyNow', 'enrichedContext', 'questions', 'decisions', 'signals', 'evidence']);
    for (const field of ['title', 'family', 'summary', 'whyNow']) {
      if (typeof content[field] !== 'string' || (content[field] as string).length > 20000) throw new Error('Texte invalide ou trop long.');
    }
    if (!(content.title as string).trim()) throw new Error('Le titre est obligatoire.');
    if (typeof content.importance !== 'string' || !['critique', 'élevée', 'moyenne'].includes(content.importance)) throw new Error('Importance invalide.');
    for (const field of ['ownerIds', 'tags', 'enrichedContext', 'questions', 'decisions', 'signals', 'evidence']) {
      const items = content[field];
      if (!Array.isArray(items) || items.length > 100 || items.some(item => typeof item !== 'string' || item.length > 20000)) throw new Error('Liste invalide ou trop longue.');
    }
    if ((content.ownerIds as string[]).some(id => !lacEpauleParticipants.some(person => person.id === id))) throw new Error('Porteur inconnu.');
  }
  return value as PreparationData;
}
export function validateStatuses(value: unknown): DiscussionStates {
  const statuses = record(value);
  if (Object.keys(statuses).length > 1000 || Object.entries(statuses).some(([id, status]) => !/^[a-z0-9-]{1,100}$/i.test(id) || typeof status !== 'string' || !['a-discuter', 'en-discussion', 'complete'].includes(status))) throw new Error('Statuts de discussion invalides.');
  return value as DiscussionStates;
}
